import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import LoadDetailsModal from './LoadDetailsModal';
import BuySugarModal from './BuySugarModal';
import MoreDetailsModal from './MoreDetailsModal';
import SugarTruckMultiModal from './SugarTruckMultiModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const MarketplaceTable = ({ loads, onBookSugarOnly, onBookBundle, onSort, sortConfig, highlightedLoadId }) => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBuySugarModalOpen, setIsBuySugarModalOpen] = useState(false);
  const [isMoreDetailsModalOpen, setIsMoreDetailsModalOpen] = useState(false);
  const [sugarTruckModal, setSugarTruckModal] = useState(null);
  // For Book Sugar+Truck flow: track which load is in quantity-selection step
  const [sugarTruckQuantityLoad, setSugarTruckQuantityLoad] = useState(null);
  const [sugarTruckSelectedQty, setSugarTruckSelectedQty] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Mandi-selected offer: brief pulse on arrival, then settles back to normal —
  // the "Selected from SugarHathi Mandi" badge (driven directly off
  // highlightedLoadId, not this) is what stays visible after the pulse fades.
  const [pulsingLoadId, setPulsingLoadId] = useState(null);
  const pulseTimeoutRef = useRef(null);

  useEffect(() => {
    if (!highlightedLoadId) return;
    // Hardened against a long/image-heavy list not having finished its layout pass
    // yet at the exact instant this effect fires — requestAnimationFrame waits for
    // the next paint, then a couple of short retries cover a card that's still
    // mounting (e.g. images loading) on slower connections/devices.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(`marketplace-offer-${highlightedLoadId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < 5) {
        attempts += 1;
        setTimeout(() => requestAnimationFrame(tryScroll), 150);
      }
    };
    requestAnimationFrame(tryScroll);
    setPulsingLoadId(highlightedLoadId);
    pulseTimeoutRef.current = setTimeout(() => setPulsingLoadId(null), 2500);
    return () => clearTimeout(pulseTimeoutRef.current);
  }, [highlightedLoadId]);

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return <Icon name="ChevronsUpDown" size={13} className="text-slate-500" />;
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={13} className="text-amber-400" />
      : <Icon name="ChevronDown" size={13} className="text-amber-400" />;
  };

  const handleBuySugar = (load) => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setSelectedLoad(load); setIsBuySugarModalOpen(true);
  };
  const handleMoreDetails = (load) => { setSelectedLoad(load); setIsMoreDetailsModalOpen(true); };

  const handleBuySugarConfirm = (purchaseData) => {
    if (selectedLoad) { onBookSugarOnly(purchaseData || selectedLoad); setIsBuySugarModalOpen(false); setSelectedLoad(null); }
  };

  // Book Sugar + Truck: Step 1 — open quantity modal
  const handleBookSugarTruck = (load) => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setSugarTruckQuantityLoad(load);
  };

  // Book Sugar + Truck: Step 2 — quantity confirmed, open truck modal
  const handleSugarTruckQuantityConfirm = (purchaseData) => {
    const qty = purchaseData?.selectedQuantity || purchaseData?.quantity;
    setSugarTruckSelectedQty(qty);
    setSugarTruckModal({ ...sugarTruckQuantityLoad, selectedQuantity: qty });
    setSugarTruckQuantityLoad(null);
  };

  // Book Sugar + Truck: Cancel quantity step
  const handleSugarTruckQuantityClose = () => {
    setSugarTruckQuantityLoad(null);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })?.format(amount);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const gradeColor = (grade) => {
    const map = {
      'S-30': 'bg-blue-100 text-blue-700 border-blue-300',
      'S-31': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'M-30': 'bg-purple-100 text-purple-700 border-purple-300',
      'L-30': 'bg-teal-100 text-teal-700 border-teal-300'
    };
    return map?.[grade] || 'bg-slate-100 text-slate-600 border-slate-300';
  };

  return (
    <>
      <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200" style={{ background: '#ffffff' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  <button onClick={() => onSort?.('date')} className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                    Date {getSortIcon('date')}
                  </button>
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  Offer Ref
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  <button onClick={() => onSort?.('grade')} className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                    Grade {getSortIcon('grade')}
                  </button>
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  Sugar Factory
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  <button onClick={() => onSort?.('quantity')} className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                    Qty (MT) {getSortIcon('quantity')}
                  </button>
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  <button onClick={() => onSort?.('rate')} className="flex items-center gap-1.5 hover:text-amber-600 transition-colors">
                    Rate / Ton {getSortIcon('rate')}
                  </button>
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                  Protection
                </th>
                <th className="px-5 py-4 text-center text-[11px] font-bold text-slate-500 uppercase tracking-widest min-w-[260px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loads?.map((load, index) => (
                <tr
                  key={load?.id}
                  id={`marketplace-offer-${load?.id}`}
                  style={{
                    background: index % 2 === 0 ? '#ffffff' : '#faf7f2',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: pulsingLoadId === load?.id ? 'inset 0 0 0 2px rgba(217,119,6,0.5)' : 'none',
                    transition: 'background 0.15s, box-shadow 0.6s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                  onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#faf7f2'}
                >
                  {/* Date */}
                  <td className="px-5 py-5">
                    <span className="text-sm text-slate-600 font-medium whitespace-nowrap">
                      {formatDate(load?.date)}
                    </span>
                  </td>

                  {/* Offer ID */}
                  <td className="px-5 py-5">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-medium" style={{ color: '#9ca3af' }}>Offer Ref</span>
                      <span className="text-xs font-mono text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-md border border-amber-200 whitespace-nowrap">
                        {load?.readableOfferId || load?.offerId}
                      </span>
                      {highlightedLoadId === load?.id && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap" style={{ background: 'rgba(217,119,6,0.12)', color: '#b45309', border: '1px solid rgba(217,119,6,0.3)' }}>
                          <Icon name="Zap" size={9} />
                          Selected from SugarHathi Mandi
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Product */}
                  <td className="px-5 py-5">
                    <div className="flex items-center gap-2.5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${gradeColor(load?.grade)}`}>
                        {load?.grade}
                      </span>
                    </div>
                  </td>

                  {/* Sugar Factory */}
                  <td className="px-5 py-5">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>{load?.millName}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap" style={{ background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.3)' }}>
                          <Icon name="BadgeCheck" size={10} />
                          Verified
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="MapPin" size={11} className="text-slate-400" />
                        <span className="text-xs text-slate-400">{load?.city}, {load?.stateName}</span>
                      </div>
                    </div>
                  </td>

                  {/* Quantity */}
                  <td className="px-5 py-5">
                    <div className="text-sm font-bold" style={{ color: '#1e293b' }}>{load?.quantity} MT</div>
                    <div className="text-xs text-slate-400 mt-0.5">{load?.packSize}</div>
                  </td>

                  {/* Rate */}
                  <td className="px-5 py-5">
                    <div className="text-lg font-extrabold" style={{ color: '#b45309' }}>{formatCurrency(load?.rate)}</div>
                    <div className="text-xs text-slate-400 mt-0.5">+ GST</div>
                  </td>

                  {/* Protection */}
                  <td className="px-5 py-5">
                    <div className="flex flex-col gap-2">
                      {load?.sellerName ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ background: 'rgba(22,163,74,0.12)', color: '#15803d', border: '1px solid rgba(22,163,74,0.35)' }}>
                          <Icon name="BadgeCheck" size={11} />
                          Verified Trader Offer
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ background: 'rgba(8,145,178,0.1)', color: '#0e7490', border: '1px solid rgba(8,145,178,0.3)' }}>
                          <Icon name="Shield" size={11} />
                          Verified Sugar Mill
                        </span>
                      )}
                      {load?.trucksAvailable > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.25)' }}>
                          <Icon name="Truck" size={11} />
                          {load?.trucksAvailable} trucks ready
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-5">
                    <div className="flex flex-col gap-2">
                      {(!user || !user?.id || !['buyer', 'admin'].includes(userProfile?.role)) ? null : (
                        <>
                          <button
                            onClick={() => handleBuySugar(load)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-extrabold transition-all duration-150 shadow-md"
                            style={{ background: '#d97706', color: '#ffffff', boxShadow: '0 4px 12px rgba(217,119,6,0.35)' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#b45309'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#d97706'; }}
                          >
                            <Icon name="ShoppingCart" size={14} />
                            Buy Sugar
                          </button>
                          <button
                            onClick={() => handleBookSugarTruck(load)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-150"
                            style={{ background: '#92400e', color: '#ffffff', boxShadow: '0 4px 12px rgba(146,64,14,0.3)' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#78350f'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#92400e'; }}
                          >
                            <Icon name="Truck" size={14} />
                            Book Sugar + Truck
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer note */}
      </div>
      {isDetailsModalOpen && selectedLoad && (
        <LoadDetailsModal
          load={selectedLoad}
          onClose={() => { setIsDetailsModalOpen(false); setSelectedLoad(null); }}
          onBookSugarOnly={onBookSugarOnly}
          onBookBundle={onBookBundle}
        />
      )}
      {isBuySugarModalOpen && selectedLoad && (
        <BuySugarModal
          load={selectedLoad}
          onClose={() => { setIsBuySugarModalOpen(false); setSelectedLoad(null); }}
          onConfirm={handleBuySugarConfirm}
        />
      )}
      {isMoreDetailsModalOpen && selectedLoad && (
        <MoreDetailsModal
          load={selectedLoad}
          onClose={() => { setIsMoreDetailsModalOpen(false); setSelectedLoad(null); }}
          onBookSugarOnly={onBookSugarOnly}
          onBookBundle={onBookBundle}
        />
      )}
      {sugarTruckModal && (
        <SugarTruckMultiModal
          load={sugarTruckModal}
          selectedQuantity={sugarTruckModal?.selectedQuantity}
          onClose={() => { setSugarTruckModal(null); setSugarTruckSelectedQty(null); }}
          onBackToQuantity={() => {
            const loadData = { ...sugarTruckModal };
            setSugarTruckModal(null);
            setSugarTruckQuantityLoad(loadData);
          }}
        />
      )}
      {sugarTruckQuantityLoad && (
        <BuySugarModal
          load={sugarTruckQuantityLoad}
          onClose={handleSugarTruckQuantityClose}
          onConfirm={handleSugarTruckQuantityConfirm}
          forTruck={true}
        />
      )}
      {showLoginModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-5" style={{ color: '#6b7280' }}>Please login or register to place your order.</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  const next = encodeURIComponent(window.location.pathname + window.location.search);
                  navigate(`/login-authentication?next=${next}`);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  const next = encodeURIComponent(window.location.pathname + window.location.search);
                  navigate(`/user-registration?next=${next}`);
                }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button onClick={() => setShowLoginModal(false)} className="mt-4 text-xs underline" style={{ color: '#9ca3af' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceTable;